import { InputHTMLAttributes, ReactElement, ReactNode, forwardRef } from 'react'
import styled from 'styled-components'

import { ErrorMessage } from 'components/v2/ErrorMessage'

import { AngleDownIcon } from 'components/Icons/AngleDown'
import { SpinnerIcon } from 'components/Icons/SpinnerIcon'
import { Label } from 'components/v2/Label'

const getDynamicWidth = (value?: string, placeholder?: string): string => {
  if (!value && !placeholder) return 'auto'

  const base = value ? value.length : placeholder?.length ?? 0
  return `${base * 10 + 5}px`
}

const InputContainer = styled.label``

const StyledAngleDownIcon = styled(AngleDownIcon)<{ isOpen?: boolean }>`
  fill: #717171;
  height: 6px !important;
  transform: ${({ isOpen }): string => (isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: 0.2s;
  width: 10px !important;
`

interface IInputContentProps {
  disabled?: boolean
  showError?: boolean
  small?: boolean
  noPadding?: boolean
  noBorder?: boolean
  width: string
}

export const InputContent = styled.div<IInputContentProps>`
  align-items: center;
  background-color: ${({ disabled }): string => (disabled ? '#F8F8F8' : '#ffffff')};
  border: 1px solid
    ${({ showError, noBorder }): string => (noBorder ? 'transparent' : showError ? '#FF627B' : '#EAE9E9')};
  border-radius: 4px;
  cursor: ${({ disabled }): string => (disabled ? 'not-allowed' : 'text')};
  display: flex;
  gap: 8px;
  padding: ${({ small, noPadding }): string => (noPadding ? '0px' : small ? '8px' : '12px')};
  width: ${({ width }): string => width};
  max-width: 100%;

  &:hover,
  &:focus-within {
    border: 1px solid
      ${({ disabled, noBorder }): string => (disabled ? '#f0f0f0' : noBorder ? 'transparent' : '#717171')};
  }

  svg {
    fill: ${({ disabled }): string => (disabled ? '#B0B0B0' : '#916EE8')};
    height: 20px;
    width: 20px;
  }

  ${StyledAngleDownIcon} {
    fill: #676879;
  }
`

interface IStyledInputProps {
  small?: boolean
  hasDynamicWidth?: boolean
}
const StyledInput = styled.input<IStyledInputProps>`
  appearance: none;
  background-color: transparent;
  border: none;
  border-color: transparent;
  color: #28265d;
  cursor: initial;
  flex: 1;
  font-size: ${({ small }): string => (small ? '14px' : '16px')};
  outline: none;
  width: 100%;
  padding: 0;

  &:disabled {
    color: #b0b0b0;
    cursor: not-allowed;
  }

  &:focus {
    border: none;
  }

  &:-webkit-autofill,
  &:-webkit-autofill:focus {
    transition: background-color 600000s 0s, color 600000s 0s, font-size 600000s 0s;
  }
`

const StyledSpinnerIcon = styled(SpinnerIcon)`
  fill: #717171;
`

export interface IInputElementProps<T> extends InputHTMLAttributes<T> {
  as?: TSFixMe
  label?: ReactNode
  iconLeft?: ReactNode
  iconRight?: ReactNode
  loading?: boolean
  isInvalid?: boolean
  small?: boolean
  noPadding?: boolean
  noBorder?: boolean
  hasDynamicWidth?: boolean
  isDropdown?: boolean
  isOpen?: boolean
  showError?: boolean
  ref?: React.Ref<T>
}

export const InputElement = forwardRef<HTMLInputElement, IInputElementProps<HTMLInputElement>>(function _InputElement(
  props: IInputElementProps<HTMLInputElement>,
  ref
): ReactElement {
  const {
    small,
    noPadding,
    noBorder,
    iconLeft,
    iconRight,
    loading,
    isDropdown,
    isOpen,
    hasDynamicWidth,
    showError,
    isInvalid,
    ...inputProps
  } = props
  const disabled = Boolean(inputProps.disabled || inputProps.readOnly)
  return (
    <InputContent
      showError={showError || isInvalid}
      disabled={disabled}
      small={small}
      noPadding={noPadding}
      noBorder={noBorder}
      width={hasDynamicWidth ? getDynamicWidth(inputProps.value as string, inputProps.placeholder) : 'auto'}
    >
      {iconLeft}
      <StyledInput ref={ref} small={small} disabled={disabled} {...inputProps} />
      {loading && <StyledSpinnerIcon />}
      {isDropdown && !loading ? <StyledAngleDownIcon isOpen={isOpen} /> : null}
      {!isDropdown && !loading && iconRight ? iconRight : null}
    </InputContent>
  )
})

export interface IInputProps<T> extends IInputElementProps<T> {
  className?: string
  label?: ReactNode
  errors?: TSFixMe
  optional?: boolean
  touched?: TSFixMe
}

export const Input = forwardRef<HTMLInputElement, IInputProps<HTMLInputElement>>(function _Input(
  { className, label, errors, optional, touched, ...inputProps }: IInputProps<HTMLInputElement>,
  ref
): ReactElement {
  const showError = Boolean(touched && errors)

  return (
    <InputContainer className={className}>
      {label && <Label optional={optional}>{label}</Label>}
      <InputElement {...inputProps} ref={ref} />
      {showError && <ErrorMessage>{errors}</ErrorMessage>}
    </InputContainer>
  )
})
